import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useBillCycleList } from "../hooks";
import { BulkBillFilters } from "./bulkBillFilters";

interface BulkBillFiltersType {
  consumerRange: string;
  status: string;
  run_date_start: string;
  run_date_end: string;
  daysBetweenRuns: string;
  page: number;
}

// Field mapping for sorting
const SORT_FIELD_MAPPING: Record<string, string> = {
  billCycleName: "bill_cycle_name",
  createdDate: "created_date",
  createdUserRemoteName: "last_modified_user_remote_name",
  consumerCount: "consumer_count",
};

const getStatusBadgeConfig = (status: string) => {
  switch (status) {
    case "completed":
      return {
        variant: "default" as const,
        className: "bg-green-100 text-green-800",
      };
    case "draft":
      return {
        variant: "secondary" as const,
        className: "bg-yellow-100 text-yellow-800",
      };
    default:
      return {
        variant: "outline" as const,
        className: "bg-gray-100 text-gray-800",
      };
  }
};

export const BulkBillTable = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: "asc" | "desc";
  } | null>(null);
  const [filters, setFilters] = useState<BulkBillFiltersType>({
    status: "all",
    run_date_start: null,
    run_date_end: null,
    daysBetweenRuns: "all",
    page: 1,
    consumerRange: "all",
  });

  const consumerRangeMapping: Record<string, { min: number; max: number }> = {
    small: { min: 0, max: 499 }, // exclusive upper bound of 500
    medium: { min: 500, max: 1000 },
    large: { min: 1001, max: 100000000 },
  };

  const formatFiltersForHook = (
    filters: BulkBillFiltersType
  ): Record<string, any> => {
    const hookFilters: Record<string, any> = {
      page: currentPage,
      limit: PAGE_SIZE,
    };
    console.log(filters);

    // Add search term
    if (searchTerm.trim()) {
      hookFilters.search = searchTerm;
    }

    // Add sorting - map the field and apply +/- prefix
    if (sortField) {
      hookFilters.sort = sortField;
    }

    if (filters.daysBetweenRuns && filters.daysBetweenRuns !== "all") {
      hookFilters.daysBetweenRuns = filters.daysBetweenRuns;
    }

    // Add date filters
    if (filters.run_date_start) {
      hookFilters.run_date_start = filters.run_date_start;
    }
    if (filters.run_date_end) {
      hookFilters.run_date_end = filters.run_date_end;
    }

    if (filters.consumerRange && filters.consumerRange !== "all") {
      const range = consumerRangeMapping[filters.consumerRange];
      if (range) {
        hookFilters.consumer_count_min = range.min;
        hookFilters.consumer_count_max = range.max;
      }
    }
    return hookFilters;
  };

  const { data: bulkBillData, isLoading } = useBillCycleList(
    formatFiltersForHook(filters)
  );

  const handleSort = (field: string, direction: "asc" | "desc") => {
    // Map the frontend field to backend field
    const mappedField = SORT_FIELD_MAPPING[field] || field;

    // Create sort parameter with +/- prefix for API
    const sortParam =
      direction === "asc" ? `+${mappedField}` : `-${mappedField}`;

    // Update both API sort field and UI sort config
    setSortField(sortParam);
    setSortConfig({ field, direction });
    setCurrentPage(1);
  };

  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const handleFiltersChange = (newFilters: BulkBillFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: "all",
      run_date_start: null,
      run_date_end: null,
      daysBetweenRuns: "all",
      page: 1,
      consumerRange: "all",
    });
    setSearchTerm("");
    setSortField(null);
    setSortConfig(null);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleView = (row: any) => {
    navigate(`/bx/bill-setup/billcycle/detail/${row.billSchedule.id}`);
  };

  const CURRENCY = useCurrency();

  // Define columns for the generic table - only 4 sortable columns as requested
  const columns: TableColumn<any>[] = [
    {
      key: "billCycleName",
      header: "Bill Cycle Name",
      type: "text",
      sortable: true,
      render: (value: any, row: any) => `${row.billSchedule.billCycleName}`,
    },
    {
      key: "createdDate",
      header: "Run Date",
      type: "text",
      sortable: true,
    },
    {
      key: "createdUserRemoteName",
      header: "Initiated By",
      type: "text",
      sortable: true,
    },
    {
      key: "daysBetweenRuns",
      header: "Days Between Runs",
      type: "custom",
      sortable: false,
      render: (value: number) => `${value} days`,
    },
    {
      key: "consumerCount",
      header: "Consumers",
      type: "custom",
      sortable: true,
    },
    {
      key: "actualVsEstimated",
      header: "Actual vs Estimate",
      type: "custom",
      sortable: false,
      render: (value: any) => `${value?.actualPercentage} %`,
    },
    {
      key: "billedAmount",
      header: "Billed Amount",
      type: "custom",
      sortable: false,
      render: (value: number) => `${CURRENCY} ${value}`,
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "badge",
      sortable: false,
      badgeConfig: {
        getVariant: (value: string) => getStatusBadgeConfig(value).variant,
        getClassName: (value: string) => getStatusBadgeConfig(value).className,
      },
    },
  ];

  // Define actions for the generic table
  const actions: TableAction[] = [
    {
      label: "View",
      onClick: handleView,
      icon: "Eye",
      variant: "outline",
    },
  ];

  // Calculate pagination info
  const totalItems = bulkBillData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <div className="space-y-4">
      <GenericTable
        data={bulkBillData?.result || []}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        searchPlaceholder="Search by cycle name or initiator..."
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        // onSort={handleSort}
        // sortConfig={sortConfig}
        actions={actions}
        actionsType="icons"
        emptyMessage="No bulk bills found"
        filterPanel={
          <BulkBillFilters
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onApplyFilters={handleFiltersChange}
            onClearFilters={clearFilters}
            initiators={[]}
          />
        }
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage: PAGE_SIZE,
          startItem,
          endItem,
        }}
        onPageChange={handlePageChange}
      />
    </div>
  );
};
